/**
 * OnboardingTooltip - Contextual tooltip for first-time users
 *
 * Shows helpful hints on first visit to each key area.
 * Dismisses permanently when user clicks "Got it" or "Skip all".
 */

import { useState, useEffect, useRef } from 'react';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface OnboardingTooltipProps {
  id: string;
  title: string;
  description: string;
  position?: TooltipPosition;
  show: boolean;
  onDismiss: () => void;
  onSkipAll: () => void;
  isLast?: boolean;
}

export function OnboardingTooltip({
  id,
  title,
  description,
  position = 'bottom',
  show,
  onDismiss,
  onSkipAll,
  isLast = false,
}: OnboardingTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Animate in
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [show]);

  if (!show) return null;

  // Position classes
  const positionClasses: Record<TooltipPosition, string> = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-3',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-3',
    left: 'right-full top-1/2 -translate-y-1/2 mr-3',
    right: 'left-full top-1/2 -translate-y-1/2 ml-3',
  };

  // Arrow classes
  const arrowClasses: Record<TooltipPosition, string> = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-green-600 border-x-transparent border-b-transparent',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-green-600 border-x-transparent border-t-transparent',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-green-600 border-y-transparent border-r-transparent',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-green-600 border-y-transparent border-l-transparent',
  };

  return (
    <div
      ref={tooltipRef}
      className={`absolute z-50 ${positionClasses[position]} transition-all duration-200 ${
        isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
      }`}
      role="tooltip"
      aria-labelledby={`${id}-title`}
    >
      {/* Tooltip card */}
      <div className="bg-slate-800 border border-green-600 rounded-lg shadow-xl p-4 max-w-xs">
        {/* Arrow */}
        <div
          className={`absolute w-0 h-0 border-8 ${arrowClasses[position]}`}
          aria-hidden="true"
        />

        {/* Content */}
        <h4
          id={`${id}-title`}
          className="text-sm font-semibold text-green-400 mb-1"
        >
          {title}
        </h4>
        <p className="text-sm text-slate-300 mb-3">{description}</p>

        {/* Actions */}
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={onSkipAll}
            className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            Skip all tips
          </button>
          <button
            onClick={onDismiss}
            className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded transition-colors"
          >
            {isLast ? 'Done' : 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Wrapper component that provides relative positioning for tooltip
 */
interface OnboardingTargetProps {
  children: React.ReactNode;
  tooltipId: string;
  tooltipTitle: string;
  tooltipDescription: string;
  tooltipPosition?: TooltipPosition;
  show: boolean;
  onDismiss: () => void;
  onSkipAll: () => void;
  isLast?: boolean;
  className?: string;
}

export function OnboardingTarget({
  children,
  tooltipId,
  tooltipTitle,
  tooltipDescription,
  tooltipPosition = 'bottom',
  show,
  onDismiss,
  onSkipAll,
  isLast = false,
  className = '',
}: OnboardingTargetProps) {
  return (
    <div className={`relative ${className}`}>
      {children}
      <OnboardingTooltip
        id={tooltipId}
        title={tooltipTitle}
        description={tooltipDescription}
        position={tooltipPosition}
        show={show}
        onDismiss={onDismiss}
        onSkipAll={onSkipAll}
        isLast={isLast}
      />
      {/* Highlight ring when tooltip is showing */}
      {show && (
        <div
          className="absolute inset-0 ring-2 ring-green-500 ring-opacity-50 rounded-lg pointer-events-none animate-pulse"
          aria-hidden="true"
        />
      )}
    </div>
  );
}
